import { apiClient, apiClientPaginated, apiBlob, ApiError } from '@/lib/api-client';
import type { SalesOrder, SalesOrderPayment, SalesOrderStatus } from '@/features/sales-orders';
import type {
  RecordSalesReceiptPayload, SalesInvoice, SalesInvoiceListParams, SalesInvoiceListResponse, SalesInvoiceStats,
} from './types';

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) return fallback;
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== false);
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

const EMPTY_STATS: SalesInvoiceStats = {
  open_count: 0, partially_paid_count: 0, paid_count: 0, overdue_count: 0, outstanding_total: 0, overdue_total: 0,
  received_this_month: 0, advances_held: 0,
};

type RecordedPayment = SalesOrderPayment & { sales_order_status: SalesOrderStatus };

export const salesInvoicesApi = {
  // Not wrapped in withFallback — a dead endpoint must surface as an error, not "no invoices".
  list: (params: SalesInvoiceListParams) =>
    apiClientPaginated<SalesInvoiceListResponse['data']>(
      `/sales-invoices${buildQuery({
        page: params.page, limit: params.limit, status: params.status || undefined, search: params.search,
        customer_name: params.customer_name, mobile: params.mobile, invoice_number: params.invoice_number,
        start_date: params.start_date, end_date: params.end_date, overdue: params.overdue, location_id: params.location_id,
      })}`,
    ) as Promise<SalesInvoiceListResponse>,

  /** Every invoice the list filters match as CSV — written by the API from all pages (T27). */
  exportCsv: (params: Omit<SalesInvoiceListParams, 'page' | 'limit'>) =>
    apiBlob(
      `/sales-invoices/export${buildQuery({
        status: params.status || undefined, search: params.search,
        customer_name: params.customer_name, mobile: params.mobile, invoice_number: params.invoice_number,
        start_date: params.start_date, end_date: params.end_date, overdue: params.overdue, location_id: params.location_id,
      })}`,
    ),

  stats: (locationId?: string) =>
    withFallback(() => apiClient<SalesInvoiceStats>(`/sales-invoices/stats${buildQuery({ location_id: locationId })}`), EMPTY_STATS),

  getById: (id: string) => withFallback(() => apiClient<SalesInvoice>(`/sales-invoices/${id}`), null),

  /** A4 PDF of the invoice; not wrapped in withFallback — a 404 must surface. */
  downloadPdf: (id: string) => apiBlob(`/sales-invoices/${id}/pdf`),

  // Money movements go through the sales order, which owns the rules (caps, applied advances, audit).
  recordPayment: (soId: string, invoiceId: string, payload: RecordSalesReceiptPayload) =>
    apiClient<RecordedPayment>(`/sales-orders/${soId}/payments`, { method: 'POST', body: { ...payload, sales_invoice_id: invoiceId } }),

  recordAdvance: (soId: string, payload: RecordSalesReceiptPayload) =>
    apiClient<RecordedPayment>(`/sales-orders/${soId}/payments`, { method: 'POST', body: payload }),

  cancel: (soId: string, invoiceId: string, reason?: string) =>
    apiClient<SalesOrder>(`/sales-orders/${soId}/invoices/${invoiceId}/cancel`, { method: 'POST', body: reason ? { reason } : {} }),
};
